import styled from '@emotion/styled';
import type { SxProps, Theme } from '@mui/material';

export const formAreaSx: SxProps<Theme> = {
    '& .MuiTextField-root, & .MuiFormControl-root': {
        width: '100%',
    },
    '& .MuiInputLabel-root': {
        color: '#64748b',
        fontWeight: 500,
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        backgroundColor: 'rgba(255, 255, 255, 0.82)',
        boxShadow: '0 1px 0 rgba(255, 255, 255, 0.85)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '& fieldset': {
            borderColor: 'rgba(148, 163, 184, 0.28)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(131, 83, 226, 0.38)',
        },
        '&.Mui-focused': {
            boxShadow: '0 0 0 4px rgba(131, 83, 226, 0.08)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#8353e2',
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: '4px',
    },
};

export const toggleLabelSx: SxProps<Theme> = {
    ml: 0,
    justifyContent: 'space-between',
    width: '100%',
    flexDirection: 'row-reverse',
    '& .MuiFormControlLabel-label': {
        fontSize: '14px',
        fontWeight: 500,
        color: '#334155',
    },
};

export const switchSx: SxProps<Theme> = {
    '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#8353e2',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#8353e2',
    },
};

export const longOptionsMenuProps = {
    slotProps: {
        paper: {
            sx: {
                maxWidth: 'min(92vw, 560px)',
                borderRadius: '14px',
                border: '1px solid rgba(148, 163, 184, 0.18)',
                boxShadow: '0 18px 44px rgba(15, 23, 42, 0.10), 0 2px 10px rgba(15, 23, 42, 0.04)',
                '& .MuiMenuItem-root': {
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    fontSize: '14px',
                    color: '#111827',
                },
                '& .MuiMenuItem-root:hover': {
                    background: 'rgba(131, 83, 226, 0.08)',
                },
                '& .MuiMenuItem-root.Mui-selected': {
                    background: 'rgba(131, 83, 226, 0.12)',
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                    background: 'rgba(131, 83, 226, 0.16)',
                },
            },
        },
    },
};

export const ResultHero = styled.div<{ passed: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 22px 20px;
    border-radius: 18px;
    border: 1px solid
        ${({ passed }) => (passed ? 'rgba(34, 197, 94, 0.24)' : 'rgba(239, 68, 68, 0.24)')};
    background: ${({ passed }) =>
        passed
            ? 'radial-gradient(circle at top, rgba(34, 197, 94, 0.12), transparent 60%), linear-gradient(180deg, #ffffff 0%, #f7fdf9 100%)'
            : 'radial-gradient(circle at top, rgba(239, 68, 68, 0.10), transparent 60%), linear-gradient(180deg, #ffffff 0%, #fff7f7 100%)'};
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
`;

export const HeroBadge = styled.span<{ passed: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    background: ${({ passed }) => (passed ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)')};
    color: ${({ passed }) => (passed ? '#15803d' : '#b91c1c')};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`;

export const HeroScore = styled.div<{ color: string }>`
    margin-top: 4px;
    color: ${({ color }) => color};
    font-size: 40px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1;
`;

export const HeroDetails = styled.div`
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
`;

export const CategoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const CategoryItem = styled.div`
    padding: 14px 16px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.85);
`;

export const CategoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 6px;
`;

export const CategoryTitle = styled.div`
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
`;

export const CategoryBadge = styled.span<{ passed: boolean }>`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${({ passed }) => (passed ? 'rgba(34, 197, 94, 0.14)' : 'rgba(239, 68, 68, 0.14)')};
    color: ${({ passed }) => (passed ? '#15803d' : '#b91c1c')};
    border: 1px solid
        ${({ passed }) => (passed ? 'rgba(34, 197, 94, 0.28)' : 'rgba(239, 68, 68, 0.28)')};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
`;

export const CategoryExplanation = styled.p`
    margin: 0;
    color: #475569;
    font-size: 13px;
    line-height: 1.5;
`;

export const AdviceBlock = styled.div`
    padding: 16px 18px;
    border: 1px solid rgba(131, 83, 226, 0.18);
    border-radius: 16px;
    background:
        radial-gradient(circle at top right, rgba(131, 83, 226, 0.08), transparent 50%),
        linear-gradient(180deg, #ffffff 0%, #fbf9ff 100%);
`;

export const AdviceTitle = styled.div`
    color: #6d41d8;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin-bottom: 6px;
`;

export const AdviceBody = styled.p`
    margin: 0;
    color: #334155;
    font-size: 14px;
    line-height: 1.55;
    white-space: pre-wrap;
`;

export const EmptyScores = styled.div`
    padding: 14px 16px;
    border-radius: 14px;
    background: rgba(148, 163, 184, 0.08);
    color: #64748b;
    font-size: 13px;
    font-style: italic;
    text-align: center;
`;
